// Sheet — the surface every modal, picker, menu and form uses at ≤880px
// (mobile overhaul Phase 0 §0.2; mockup §0 "Sheets", drawn in §3.3 H and
// §1.2 D). Two variants:
//   full  — forms. Pinned 46px from the top; header carries Cancel / title /
//           primary; the primary is repeated in a sticky footer that tracks
//           the software keyboard so it stays reachable while typing.
//   half  — pickers, menus, single fields. Content height, capped at 85vh.
// Grab handle (drag down > 80px closes), backdrop tap closes, Escape closes
// the top-most sheet only. Sheets stack: each one adds its own backdrop, so
// a second sheet over a first darkens further. While any sheet is open,
// <body data-sheet-open> is set — the FAB hides and the TabBar stops taking
// taps (§0.2.3). Tokens only; light theme needs no overrides.

import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

export type SheetDismissReason = 'escape' | 'backdrop' | 'drag';

export interface SheetProps {
  /** Leave Escape to nested controls (e.g. drag cancellation), or block dismissal while saving. */
  canDismiss?: (reason: SheetDismissReason) => boolean;
  open: boolean;
  onClose: () => void;
  /** full: top 46px, forms. half: content height, max 85vh, pickers/menus. */
  variant: 'full' | 'half';
  title?: ReactNode;
  /** Mono eyebrow under the title (mockup .ctx) — "ex GST", "Task · Project". */
  context?: string;
  /** "Cancel" / "Back" — a text button, 44px tall. Its presence centres the title. */
  leftAction?: ReactNode;
  /** "Create" / "Save" / "Close" — accent text button. */
  rightAction?: ReactNode;
  /** Pinned between the header and the scrolling body (a search field,
      the palette input) — never scrolls away. */
  pinned?: ReactNode;
  /** Sticky footer; padding-bottom = 30px + env(safe-area-inset-bottom). */
  footer?: ReactNode;
  /** Extra class on the .sheet element (page-scoped styling hooks). */
  className?: string;
  /** Accessible name when there's no visible title. */
  ariaLabel?: string;
  children: ReactNode;
}

// ── Open-sheet registry: Escape targets the top sheet only; body flag ──
const stack: { id: symbol; el: HTMLElement | null }[] = [];
const listeners = new Set<() => void>();
function syncBody() {
  if (typeof document === 'undefined') return;
  if (stack.length > 0) document.body.dataset.sheetOpen = '1';
  else delete document.body.dataset.sheetOpen;
  listeners.forEach((fn) => fn());
}
function pushSheet(id: symbol, el: HTMLElement | null) {
  stack.push({ id, el });
  syncBody();
}
function popSheet(id: symbol) {
  const i = stack.findIndex((s) => s.id === id);
  if (i >= 0) stack.splice(i, 1);
  syncBody();
}
function isTopSheet(id: symbol): boolean {
  return stack[stack.length - 1]?.id === id;
}
/** Subscribe to "any sheet open?" changes — Shell hides the FAB on it. */
export function subscribeSheetOpen(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
export function anySheetOpen(): boolean {
  return stack.length > 0;
}

const DRAG_CLOSE_PX = 80;

/** Distance the sheet's bottom edge must rise to clear the software
    keyboard: layout height minus the visual viewport. Zero when the
    keyboard is down or `visualViewport` is missing. Pure so it's testable. */
export function keyboardInset(layoutHeight: number, visual: { height: number; offsetTop: number } | null): number {
  if (!visual) return 0;
  return Math.max(0, Math.round(layoutHeight - visual.height - visual.offsetTop));
}

export function Sheet({
  open,
  onClose,
  canDismiss,
  variant,
  title,
  context,
  leftAction,
  rightAction,
  pinned,
  footer,
  className,
  ariaLabel,
  children,
}: SheetProps) {
  const idRef = useRef<symbol | null>(null);
  if (idRef.current === null) idRef.current = Symbol('sheet');
  const id = idRef.current;
  const sheetRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const canDismissRef = useRef(canDismiss);
  canDismissRef.current = canDismiss;
  const [inset, setInset] = useState(0);
  const [drag, setDrag] = useState<{ startY: number; dy: number } | null>(null);
  const titleId = useId();

  // Register on the stack while open; restore focus to the opener on close.
  useLayoutEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    pushSheet(id, sheetRef.current);
    return () => {
      popSheet(id);
      // Focus goes to whichever sheet is still on top; only when none is
      // left does it return to the opener (if it still exists).
      const top = stack[stack.length - 1];
      if (top?.el && document.contains(top.el)) {
        if (!top.el.contains(document.activeElement)) top.el.focus({ preventScroll: true });
        return;
      }
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus();
    };
  }, [open, id]);

  // Initial focus: the first text control (so the keyboard comes up for
  // forms and searches), else the dialog itself — never a row button, which
  // would open every menu with its first row ringed.
  useEffect(() => {
    if (!open) return;
    const el = sheetRef.current;
    if (!el) return;
    const t = window.setTimeout(() => {
      if (el.contains(document.activeElement)) return;
      const first = el.querySelector<HTMLElement>(
        'input:not([type=hidden]), textarea, select, [data-sheet-autofocus]',
      );
      (first ?? el).focus({ preventScroll: true });
    }, 30);
    return () => window.clearTimeout(t);
  }, [open]);

  // Escape closes the top-most sheet only; Tab stays inside the sheet.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (!isTopSheet(id)) return;
      if (e.key === 'Escape') {
        if (canDismissRef.current?.('escape') === false) return;
        e.stopPropagation();
        e.preventDefault();
        closeRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const el = sheetRef.current;
      if (!el) return;
      const focusables = Array.from(
        el.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]):not([type=hidden]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((n) => n.offsetParent !== null);
      if (focusables.length === 0) {
        e.preventDefault();
        el.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      // From the dialog itself (initial focus on menus) or from anywhere
      // outside, Tab lands on the first control and Shift+Tab on the last.
      if (!active || !focusables.includes(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    // Capture phase so the desktop Modal / Dropdown handlers underneath
    // never also fire for the same Escape.
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, id]);

  // Track the software keyboard: lift the sheet's bottom edge by the
  // difference between the layout and visual viewports so the footer's
  // primary button stays above the keys (§0 "primary repeated above the
  // keyboard"). No-op where visualViewport is missing.
  useEffect(() => {
    if (!open) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => setInset(keyboardInset(window.innerHeight, { height: vv.height, offsetTop: vv.offsetTop }));
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [open]);

  if (!open) return null;

  const onHandleDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setDrag({ startY: e.clientY, dy: 0 });
  };
  const onHandleMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag) return;
    setDrag({ startY: drag.startY, dy: Math.max(0, e.clientY - drag.startY) });
  };
  const onHandleUp = () => {
    if (!drag) return;
    const shouldClose = drag.dy > DRAG_CLOSE_PX;
    setDrag(null);
    if (shouldClose && canDismissRef.current?.('drag') !== false) closeRef.current();
  };

  const hasHead = title !== undefined || context !== undefined || leftAction !== undefined || rightAction !== undefined;
  const style: React.CSSProperties = {};
  if (inset > 0) style.bottom = inset;
  if (drag && drag.dy > 0) {
    style.transform = `translateY(${drag.dy}px)`;
    style.transition = 'none';
  }

  return createPortal(
    <div className="sheet-root" data-variant={variant}>
      <div className="sheet-backdrop" onClick={() => { if (canDismissRef.current?.('backdrop') !== false) closeRef.current(); }} />
      <div
        ref={sheetRef}
        className={`sheet sheet-${variant}${className ? ` ${className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title !== undefined ? titleId : undefined}
        aria-label={title === undefined ? ariaLabel : undefined}
        tabIndex={-1}
        style={style}
        // Clicks inside must not reach the desktop overlay/anchor handlers
        // of whatever opened us (Dropdown anchors toggle on mousedown).
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="sheet-grab"
          onPointerDown={onHandleDown}
          onPointerMove={onHandleMove}
          onPointerUp={onHandleUp}
          onPointerCancel={onHandleUp}
          aria-hidden="true"
        >
          <span />
        </div>
        {hasHead && (
          <div className={`sheet-head${leftAction !== undefined ? ' centred' : ''}`}>
            <div className="sheet-act left">{leftAction}</div>
            <div className="sheet-heading">
              {title !== undefined && (
                <div className="sheet-title" id={titleId}>
                  {title}
                </div>
              )}
              {context !== undefined && <div className="sheet-ctx">{context}</div>}
            </div>
            <div className="sheet-act right">{rightAction}</div>
          </div>
        )}
        {pinned !== undefined && <div className="sheet-pinned">{pinned}</div>}
        <div className="sheet-body">{children}</div>
        {footer !== undefined && <div className="sheet-foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/** Header text button — "Cancel" (plain) or "Create" / "Save" (accent). */
export function SheetButton({
  tone,
  onClick,
  disabled,
  children,
}: {
  tone?: 'accent' | 'danger';
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={`sheet-act-btn${tone ? ` ${tone}` : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

/** One tappable row inside a sheet (mockup .mrow): icon · label (+ small
    secondary line) · trailing detail. Shared by the More sheet, quick-add,
    PageMenu, PickerSheet and every row "···" sheet. */
export function SheetRow({
  icon,
  label,
  sub,
  right,
  tone,
  active,
  disabled,
  onSelect,
  role = 'button',
  className = '',
}: {
  icon?: ReactNode;
  label: ReactNode;
  sub?: ReactNode;
  right?: ReactNode;
  tone?: 'danger' | 'dim';
  /** Current choice — accent text + tinted background (mockup .mrow.on). */
  active?: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  role?: 'button' | 'option' | 'menuitem';
  className?: string;
}) {
  return (
    <button
      type="button"
      role={role}
      aria-selected={role === 'option' ? !!active : undefined}
      className={`mrow${active ? ' on' : ''}${tone ? ` ${tone}` : ''}${className ? ` ${className}` : ''}`}
      disabled={disabled}
      onClick={onSelect}
    >
      {icon !== undefined && <span className="mrow-ico">{icon}</span>}
      <span className="mrow-l">
        <span className="mrow-label">{label}</span>
        {sub !== undefined && sub !== null && sub !== '' && <small>{sub}</small>}
      </span>
      {right !== undefined && <span className="mrow-r">{right}</span>}
    </button>
  );
}

/** Mono eyebrow section label between groups of rows (mockup .sec-l). */
export function SheetSection({ children }: { children: ReactNode }) {
  return <div className="sec-l">{children}</div>;
}
